import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { getAllTools, ConfigService } from '@web-client/shared';

interface SearchItem {
  title: string;
  description: string;
  route: string;
  icon: string;
  category: string;
}

@Component({
  selector: 'lib-global-search',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, FormsModule, RouterModule],
  template: `
    <div class="p-0 overflow-hidden flex flex-col w-[92vw] sm:w-[600px] md:w-[650px] h-[calc(100vh-140px)] sm:h-auto max-h-[calc(100vh-140px)] sm:max-h-[600px] rounded-2xl shadow-2xl border" 
         style="background-color: var(--mat-sys-surface); color: var(--mat-sys-on-surface); border-color: var(--mat-sys-outline-variant)">
      <!-- Search Input Area -->
      <div class="p-4 sm:p-6 border-b flex items-center gap-3 sm:gap-4 shrink-0" style="border-color: var(--mat-sys-outline-variant)">
        <mat-icon style="color: var(--mat-sys-primary)">search</mat-icon>
        <input #searchInput
          type="text" 
          [(ngModel)]="query" 
          (ngModelChange)="onSearch()"
          placeholder="Search tools, blogs..."
          class="flex-1 bg-transparent border-none outline-none text-lg sm:text-xl font-medium min-w-0"
        >
        <div class="hidden sm:flex px-2.5 py-1 rounded-md text-[10px] font-bold border shadow-sm opacity-60 uppercase tracking-tighter shrink-0" 
             style="background-color: var(--mat-sys-surface-container-high); border-color: var(--mat-sys-outline-variant); color: var(--mat-sys-on-surface-variant)">
          ESC
        </div>
        <button mat-icon-button mat-dialog-close class="sm:hidden">
            <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Results Area -->
      <div class="flex-1 overflow-y-auto custom-scrollbar min-h-0">
        @if (filteredItems.length > 0) {
          @for (category of categories; track category) {
            @if (getItemsByCategory(category).length > 0) {
              <div class="px-6 py-2 sm:py-3 text-[10px] font-bold uppercase tracking-[0.2em] opacity-50 sticky top-0 z-10" style="background-color: var(--mat-sys-surface-container-low)">
                {{ category }}
              </div>
              @for (item of getItemsByCategory(category); track item.route) {
                <div 
                  (click)="navigate(item.route)"
                  (keydown.enter)="navigate(item.route)"
                  tabindex="0"
                  role="button"
                  class="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                >
                  <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center bg-slate-100 dark:bg-slate-800 group-hover:bg-indigo-600 group-hover:text-white transition-all shrink-0">
                    <mat-icon class="!text-[20px] sm:!text-[24px]">{{ item.icon }}</mat-icon>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <div class="font-bold text-sm truncate">{{ item.title }}</div>
                    <div class="text-xs opacity-60 line-clamp-1 truncate">{{ item.description }}</div>
                  </div>
                  <mat-icon class="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hidden sm:block" style="color: var(--mat-sys-primary)">east</mat-icon>
                </div>
              }
            }
          }
        } @else if (query) {
          <div class="p-10 sm:p-20 text-center flex flex-col items-center">
            <mat-icon class="!w-12 !h-12 sm:!w-16 sm:!h-16 !text-[48px] sm:!text-[64px] opacity-20 mb-4">search_off</mat-icon>
            <div class="text-base sm:text-lg font-bold opacity-50">No results for "{{ query }}"</div>
            <p class="text-xs sm:text-sm opacity-40">Try a different search term.</p>
          </div>
        } @else {
          <div class="p-8 sm:p-12 text-center opacity-30 flex flex-col items-center">
            <mat-icon class="!w-12 !h-12 sm:!w-16 sm:!h-16 !text-[48px] sm:!text-[64px] mb-4">pageview</mat-icon>
            <p class="text-sm">Start typing to search...</p>
          </div>
        }
      </div>

      <!-- Footer -->
      <div class="p-3 sm:p-4 px-4 sm:px-6 text-[10px] border-t opacity-40 flex justify-between shrink-0 mb-safe" style="border-color: var(--mat-sys-outline-variant)">
        <span class="hidden sm:block">Press <span class="font-bold">Enter</span> to select</span>
        <span class="sm:hidden text-center flex-1">Tap an item to select</span>
        <span class="hidden sm:block"><span class="font-bold">20+</span> utilities found</span>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .custom-scrollbar::-webkit-scrollbar { width: 4px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--mat-sys-outline-variant); border-radius: 10px; }
    
    ::ng-deep .full-flat-dialog .mdc-dialog__surface {
        padding: 0 !important;
        border-radius: 20px !important;
        background: transparent !important;
        box-shadow: none !important;
    }

    /* Scoped icon styles for search results */
    :host mat-icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }
  `]
})
export class GlobalSearchComponent implements OnInit {
  private readonly config = inject(ConfigService);
  private readonly dialogRef = inject(MatDialogRef<GlobalSearchComponent>);
  private readonly router = inject(Router);

  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef<HTMLInputElement>;

  query = '';
  categories = ['Core', 'Tools', 'Blog'];

  allItems: SearchItem[] = [
    { title: 'Home', description: 'Go to landing page', route: '/', icon: 'home', category: 'Core' },
    { title: 'Apps & Utilities', description: 'Explore all available applications', route: '/apps', icon: 'apps', category: 'Core' },
    { title: 'Games', description: 'Fun and interactive games', route: '/games', icon: 'sports_esports', category: 'Core' },
    { title: 'Blogs', description: 'Latest articles and news', route: '/blogs', icon: 'article', category: 'Core' },
    ...this.getFilteredTools()
  ];

  private getFilteredTools() {
    const productionOnly = this.config.productionOnly;
    return getAllTools(productionOnly).map(tool => ({
      title: tool.title,
      description: tool.description,
      route: tool.path,
      icon: tool.icon || 'extension',
      category: tool.path.startsWith('/games') ? 'Games' : 'Tools'
    }));
  }

  filteredItems: SearchItem[] = [];

  ngOnInit() {
    setTimeout(() => this.searchInput.nativeElement.focus(), 100);
  }

  onSearch() {
    const q = this.query.toLowerCase().trim();
    if (!q) {
      this.filteredItems = [];
      return;
    }
    this.filteredItems = this.allItems.filter(item =>
      item.title.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q)
    );
  }

  getItemsByCategory(category: string) {
    return this.filteredItems.filter(i => i.category === category);
  }

  navigate(route: string) {
    this.router.navigateByUrl(route);
    this.dialogRef.close();
  }
}
